import {
  ReactElement, useEffect, useMemo, useRef,
} from 'react';
import { t } from 'i18next';

import { colors } from 'styles/colors';

import { MaintenanceViewBy } from '../../types';
import { MaintenanceAnalytics } from '../../helpers/api';
import { buildData } from './helpers';
import { StatesBarSkeleton } from './parts/StatesBarSkeleton';
import {
  Card,
  ChartArea,
  ScrollArea,
  Title,
} from './styles';
import { useGenerateStatesBar } from './useGenerateStatesBar';

const MIN_SLOT_PX = 50;

interface StatesBarProps {
  analytics: MaintenanceAnalytics | undefined;
  loading: boolean;
  viewBy: MaintenanceViewBy;
}

export function StatesBar({ analytics, loading, viewBy }: Readonly<StatesBarProps>): ReactElement {
  const chartContainerRef = useRef<HTMLDivElement>(null);

  const { renderChart } = useGenerateStatesBar({ containerRef: chartContainerRef });

  const data = useMemo(() => buildData(analytics?.STATES, viewBy), [analytics, viewBy]);
  const isEmpty = !loading && data.every((d) => d.value === 0);
  const minWidth = Math.max(data.length * MIN_SLOT_PX, 320);
  const title = viewBy === 'capacity' ? t('trPorEstado') : t('maquinasPorEstado');

  useEffect(() => {
    if (loading || isEmpty) return;
    renderChart({ data, unit: viewBy });
  }, [loading, isEmpty, data, viewBy, renderChart]);

  return (
    <Card>
      <Title>{title}</Title>
      {loading && <StatesBarSkeleton />}
      {!loading && isEmpty && (
        <ChartArea style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors.text['sub-500'],
        }}
        >
          {t('nenhumDadoEncontrado')}
        </ChartArea>
      )}
      {!loading && !isEmpty && (
        <ScrollArea>
          <ChartArea ref={chartContainerRef} style={{ minWidth }} />
        </ScrollArea>
      )}
    </Card>
  );
}
